import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { CommonValidatorService } from '../../../../../core/services/validators/common-validator.service';
import { ApiErrorService } from '../../../../../core/api/api-error.service';
import { SalesEngineService } from '../../../../../core/api/sales-engine.service';
import { ProductGet, ProductType, ProductUpdate } from '../../../../../core/interfaces/sales-engine-interfaces';
import { ToastComponent } from '../../../../../shared/components/toast/toast.component';
import { TranslocoPipe } from '@jsverse/transloco';
import { LabeledInputComponent } from "../../../../../shared/components/labeled-input/labeled-input.component";
import { TextareaInputComponent } from "../../../../../shared/components/textarea-input/textarea-input.component";
import { CustomButtonComponent } from "../../../../../shared/components/custom-button/custom-button.component";
import { firstValueFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { CustomDropdownComponent } from '../../../../../shared/components/custom-dropdown/custom-dropdown.component';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-create-edit-product',
  standalone: true,
  imports: [TranslocoPipe, LabeledInputComponent, TextareaInputComponent, CustomButtonComponent, CustomDropdownComponent, CommonModule],
  templateUrl: './create-edit-product.component.html',
  styleUrl: './create-edit-product.component.css'
})
export class CreateEditProductComponent {
  constructor(private commonValidator: CommonValidatorService, private apiError: ApiErrorService, private salesEngine: SalesEngineService) { }

  @Input() isOpen: boolean = false;
  @Output() isOpenChange = new EventEmitter<boolean>();

  @Input() isEditing: boolean = false;
  @Input() product?: ProductGet;

  @Input() toast?: ToastComponent;
  @Input() reloadCallbackFunction?: () => void;

  // Inputs
  nameInput: string = '';
  nameInputError: string | null = null;

  typeDropdownOptions: string[] = [];
  selectedTypeDropdown?: number | undefined;

  descriptionInput: string = '';
  descriptionInputError: string | null = null;

  segmentInput: string = '';
  targetSegments: string[] = [];

  // Button click
  confirmClicked: boolean = false;

  ngOnChanges(changes: SimpleChanges): void {
    this.resetData();
    this.populateStaticDropdowns();
    if (this.product != undefined) {
      this.populateInputsFromProduct(this.product)
    }
  }

  populateStaticDropdowns() {
    this.typeDropdownOptions = [];
    this.typeDropdownOptions = Object.values(ProductType);
  }

  closeView() {
    this.isOpen = false;
    this.isOpenChange.emit(this.isOpen);
  }

  populateInputsFromProduct(product: ProductGet) {
    this.nameInput = product.name || '';
    this.descriptionInput = product.description || '';
    this.selectedTypeDropdown = this.typeDropdownOptions
      .findIndex(opt => opt.toUpperCase() === (product.type ?? '').toUpperCase());
    this.targetSegments = product.target_segments || [];
  }

  resetData() {
    this.nameInput = this.descriptionInput = '';
    this.nameInputError = this.descriptionInputError = null;
    this.selectedTypeDropdown = undefined;
    this.targetSegments = [];
    this.segmentInput = '';
  }

  validateInputs(): boolean {
    const nameInputValue = this.nameInput.trim();
    const descriptionInputValue = this.descriptionInput.trim();

    // Validate length for text inputs
    const nameError = this.commonValidator.validateLength(nameInputValue, 2, 255);
    const descriptionError = this.commonValidator.validateLength(descriptionInputValue, 2, 255, false);

    // Show errors
    this.nameInputError = nameError;
    this.descriptionInputError = descriptionError;

    // If there are any errors show them and prevent submission
    if (nameError || descriptionError) {
      return false;
    }

    return true;
  }

  getProductFromInputs(id: string = ''): ProductUpdate {
    const selectedProductType = this.typeDropdownOptions[this.selectedTypeDropdown!].toUpperCase() as ProductType || null;
    const result: ProductUpdate = {
      name: this.nameInput.trim(),
      description: this.descriptionInput,
      type: selectedProductType,
      target_segments: this.targetSegments
    }
    return result;
  }

  async confirmButtonClick() {
    if (this.confirmClicked) {
      return;
    }
    this.confirmClicked = true;

    if (!this.validateInputs()) {
      this.confirmClicked = false;
      return;
    }

    // Send request for creating location
    try {
      if (this.product) {
        const newData = this.getProductFromInputs(this.product.id);
        await firstValueFrom(this.salesEngine.updateProduct(this.product.id, newData));
      }
      else {
        const newData = this.getProductFromInputs();
        await firstValueFrom(this.salesEngine.createProduct(newData));
      }
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast?.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      this.confirmClicked = false;
      return;
    }

    this.confirmClicked = false;
    this.isOpen = false;

    if (this.reloadCallbackFunction) {
      this.reloadCallbackFunction();
    }
  }

  addSegment(): void {
    const v = (this.segmentInput || '').trim();
    if (!v) return;

    if (!this.targetSegments.includes(v)) {
      this.targetSegments.push(v);
    }

    this.segmentInput = '';
  }

  removeSegment(index: number): void {
    if (index > -1 && index < this.targetSegments.length) {
      this.targetSegments.splice(index, 1);
    }
  }

}
